<template>
  <div class="groupCompare" :style="pageSetting">
    <button type="button" class="btnClose" @click.stop="close"></button>
    <div class="compareItems">
      <div class="collectionGroupTable">
        <div class="groupHeader" :style="pageSetting">
          <div v-for="classItem in classes"
            :key="classItem.collectionClassId"
            class="classHeader"
            :class="{isRequired: requiredClassMap[classItem.collectionClassId], isPassed: isPassed(classItem.collectionClassId)}">
            {{ classItem.collectionClassName[lang] }}
          </div>
          <div v-if="compareItems.hasPrice" class="classHeader groupItemPrice">Total</div>
        </div>
        <div class="groups">
          <div v-for="item in compareItems.items"
            :key="item.orderItemId || item.collectionItemId"
            class="groupRow">
            <div class="groupItemName">
              {{ item.myCollectionName }}
            </div>
            <div v-for="classItem in item.classItems"
              :key="classItem.collectionClassId"
              class="classCell"
              :class="{isChecked: isChecked(classItem, item.orderItemId)}"
              @click="toggleClassItem(item, classItem)">
              <span v-if="classItem.selectedSubItems.length > 0" class="checkbox"></span>
              <CollectionGroupItem v-for="subItem in classItem.selectedSubItems"
                :key="subItem.orderItemId || subItem.subItemId"
                :locale="locale"
                :lang="lang"
                :item="subItem"
                :categoryId="categoryId"
                :isChecked="isChecked(classItem, item.orderItemId)" />
            </div>
            <div v-if="compareItems.hasPrice" class="groupItemPrice">{{ priceFormat(item.totalPrice) }}</div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="compareItems.hasPrice" class="totalSelected" :class="{centered: !requiredSelected}">
      {{ !requiredSelected ? '선택을 완료 해주세요.' : priceFormat(selectedTotalPrice) }}
    </div>
    <div class="compareFooter">
      <div class="myCollectionGroupBtns">
        <div class="myCollectionInput">
          <div class="myCollectionFlag">
            <button
              v-for="flag in flags"
              :key="flag.id"
              type="button"
              :class="{ btnFlag: true, active: myCollectionFlag === flag.id }"
              :style="{ backgroundColor: flag.value }"
              @click.stop="toggleFlag(flag.id)"
            ></button>
          </div>
          <input
            v-model.trim="myCollectionName"
            type="text"
            class="input"
            placeholder="저장할 이름을 입력해주세요."
            :disabled="isSubmitting"
            @keydown.enter.prevent.stop="compareMyCollection" />
          <button
            type="button"
            :disabled="!validateOnSubmit || isSubmitting"
            class="addMyCollection"
            :style="{backgroundColor: pointColor}"
            @click.stop="compareMyCollection">
            <img :src="collectionIcon.default" alt="" />
            새 마이콜렉션 담기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CollectionGroupItem from './MyCollectionGroupItem'

export default {
  name: 'MyCollectionCompare',
  props: [
    'locale',
    'lang',
    'pageId',
    'categoryId',
  ],
  components: {
    CollectionGroupItem
  },
  computed: {
    setting () {
      return this.$store.getters['collection/getCollectionSetting']
    },
    pointColor () {
      return this.$store.getters['collection/getPointColor']
    },
    pageSetting () {
      return this.setting && {
        backgroundColor: this.setting.backgroundColor
      }
    },
    collectionIcon () {
      const myCollectionSetting = this.$store.getters['collection/getMyCollectionSetting']
      const scrapIcon = myCollectionSetting && {
        ...myCollectionSetting.scrapIcon,
        size: `${myCollectionSetting.scrapIcon.size}px`
      }
      return scrapIcon
    },
    compare () {
      return this.$store.getters['collection/getMyCollectionCompare']
    },
    classes () {
      return this.compare.classes || []
    },
    items () {
      return this.compare.items || []
    },
    selectedClassItems () {
      return this.compare.selectedClassItems || {}
    },
    requiredClassMap () {
      return this.classes.reduce((acc, item) => {
        acc[item.collectionClassId] = item.isRequired
        return acc
      }, {})
    },
    compareItems() {
      let hasPrice = false

      const items = this.items.map(item => {
        const totalPrice = item.totalPrice || 0

        if (totalPrice > 0) {
          hasPrice = true
        }
        return {
          ...item,
          totalPrice
        }
      })

      return {
        hasPrice,
        items
      }
    },
    selectedSubmitItems () {
      return Object.values(this.selectedClassItems)
        .filter(classItem => {
          return Array.isArray(classItem.selectedSubItems) &&
            classItem.selectedSubItems.length > 0
        })
        .map(classItem => {
          const collectionClassId = classItem.collectionClassId

          return {
            collectionClassId,
            isRequired: !!this.requiredClassMap[collectionClassId],
            subItemIds: classItem.selectedSubItems
              .map(subItem => subItem.subItemId)
              .filter(Boolean)
          }
        })
        .filter(classItem => classItem.subItemIds.length > 0)
    },
    selectedTotalPrice () {
      return Object.values(this.selectedClassItems)
        .reduce((total, classItem) => {
          const classTotal = (classItem.selectedSubItems || [])
            .reduce((sum, subItem) => {
              return sum + Number(subItem.price || 0)
            }, 0)

          return total + classTotal
        }, 0)
    },
    compareOrderItemIds () {
      return [
        ...new Set(
          this.items
            .map(item => item.orderItemId)
            .filter(Boolean)
        )
      ]
    },
    requiredSelected () {
      if (!this.selectedSubmitItems.length) {
        return false
      }
      return this.classes
        .filter(classItem => classItem.isRequired)
        .every(classItem => {
          const selected =
            this.selectedClassItems[classItem.collectionClassId]

          return Array.isArray(selected && selected.selectedSubItems) &&
            selected.selectedSubItems.length > 0
        })
    },
    validateOnSubmit () {
      if (!this.myCollectionName.trim()) {
        return false
      }
      return this.requiredSelected
    },
    flags () {
      return this.$store.getters['collection/getMyCollectionFlags']
    }
  },
  data () {
    return {
      myCollectionName: '',
      myCollectionFlag: '',
      isSubmitting: false
    }
  },
  methods: {
    isPassed (classId) {
      const selectedSubItems =
        this.selectedClassItems[classId] &&
        this.selectedClassItems[classId].selectedSubItems
      return Array.isArray(selectedSubItems) && selectedSubItems.length > 0
    },
    toggleClassItem (item, classItem) {
      if (!classItem.selectedSubItems.length) {
        return
      }

      const classId = classItem.collectionClassId
      const selected = this.selectedClassItems[classId]
      const isSameItem = selected && selected.parentOrderItemId === item.orderItemId

      if (isSameItem) {
        if (this.requiredClassMap[classId]) {
          return
        }

        this.$store.commit(
          'collection/removeMyCollectionCompareSelected',
          classId
        )
        return
      }

      this.$store.commit('collection/setMyCollectionCompareSelected', {
        collectionClassId: classId,
        collectionClassName: classItem.collectionClassName,
        parentOrderItemId: item.orderItemId,
        selectedSubItems: [...classItem.selectedSubItems]
      })
    },
    isChecked (classItem, parentOrderItemId) {
      const selectedClassItem = this.selectedClassItems[classItem.collectionClassId]
      return selectedClassItem && selectedClassItem.parentOrderItemId === parentOrderItemId
    },
    priceFormat (price) {
      return this.$store.getters['util/getPriceFormatter'](price)
    },
    toggleFlag (flag) {
      this.myCollectionFlag =
        this.myCollectionFlag === flag ? '' : flag
    },
    close () {
      this.$store.commit('collection/clearMyCollectionCompare')
    },
    async saveSnapshot () {
      try {
        const data = {
          pageId: this.$route.params.pageId,
          memo: '[auto] before compare item'
        }
        const res = await this.$store.dispatch('collection/saveMyCollectionSnapshot', data)
        return res

      } catch (err) {
        console.error(err)
        return null
      }
    },
    async removeComparedItems () {
      const collectionCategory = this.categoryId

      for (const orderItemId of this.compareOrderItemIds) {
        const res = await this.$store.dispatch(
          'collection/removeMyCollectionItem',
          {
            pageId: this.pageId,
            collectionCategory,
            orderItemId
          }
        )

        if (!res || res.status !== 200) {
          throw new Error(`기존 마이컬렉션 항목 삭제 실패: ${orderItemId}`)
        }
      }
    },
    async compareMyCollection () {
      if (this.isSubmitting || !this.validateOnSubmit) {
        return
      }

      this.isSubmitting = true
      this.$store.commit('loading', true)

      const collectionCategory = this.categoryId
      const compareOrderItemId = this.compareItems.items
        .map(item => item.orderItemId)
        .filter(Boolean)

      const data = {
        pageId: this.pageId,
        collectionCategory,
        compareOrderItemId,
        myCollectionName: this.myCollectionName.trim(),
        subItems: this.selectedSubmitItems,
        ...(this.myCollectionFlag && {
          myCollectionFlag: this.myCollectionFlag
        })
      }

      try {
        const snapshot = await this.saveSnapshot()

        if (!(snapshot && snapshot.status === 200)) {
          throw new Error('마이컬렉션 비교 이력 저장에 실패했습니다.')
        }

        const addRes = await this.$store.dispatch(
          'collection/compareCollectionItem',
          data
        )

        if (!(addRes && addRes.status === 200)) {
          throw new Error('새 마이컬렉션 항목 저장에 실패했습니다.')
        }

        await this.$store.dispatch(
          'collection/getMyCollectionItems',
          {
            pageId: this.pageId
          }
        )
        this.$emit('completeCompare')
      } catch (error) {
        console.error(error)
        alert(error.message || '마이컬렉션 저장 중 오류가 발생했습니다.')
      } finally {
        this.isSubmitting = false
        this.$store.commit('loading', false)
      }
    }
  }
}
</script>